/* eslint-disable no-useless-escape */
import React, { useState, useEffect, useCallback } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import Label from "./Label";
import Icon from "../icons/Icon";
import ErrorsForField from "../form/ErrorsForField";
import { useField } from "../form/Form";
import MaskedInput, { conformToMask } from "@biproxi/react-text-mask";

import "./Input.scss";
import { createDataAttributes } from "../../utilities";

const preDefinedMasks = {
  phone: {
    maskConfig: [
      "(",
      /[1-9]/,
      /\d/,
      /\d/,
      ")",
      " ",
      /\d/,
      /\d/,
      /\d/,
      "-",
      /\d/,
      /\d/,
      /\d/,
      /\d/
    ],
    replace: /\(|\)|-|_|\s/g,
    replaceWith: "",
    regExTest: /^\([1-9]\d\d\)\s\d\d\d\-\d\d\d\d$/,
    regExTestStripped: /^\d{10}$/
  },
  ssn: {
    maskConfig: [
      /\d/,
      /\d/,
      /\d/,
      "-",
      /\d/,
      /\d/,
      "-",
      /\d/,
      /\d/,
      /\d/,
      /\d/
    ],
    replace: /-|_/g,
    replaceWith: "",
    regExTest: /^\d\d\d\-\d\d\-\d\d\d\d$/,
    regExTestStripped: /^\d{9}$/
  },
  ein: {
    maskConfig: [/\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
    replace: /-|_/g,
    replaceWith: "",
    regExTest: /^\d\d\-\d\d\d\d\d\d\d$/,
    regExTestStripped: /^\d{9}$/
  },
  numberCommas: {
    manualMask: true,
    isNumber: true,
    process: new Intl.NumberFormat(),
    replace: /,/g,
    replaceWith: ""
  },
  currency: {
    manualMask: true,
    isNumber: true,
    process: new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }),
    replace: /,|$|\..+|\./g,
    replaceWith: ""
  },
  currencyWithDecimals: {
    manualMask: true,
    isNumber: true,
    process: new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }),
    replace: /,|$|(\.\d{2})(?:\.|\.\d|\d+)+/g,
    replaceWith: "$1"
  }
};

const Input = ({
  id,
  name,
  label,
  labelElement,
  placeholder,
  value,
  minLength,
  maxLength,
  className,
  disabled,
  onChange,
  onBlur,
  onKeyDown,
  onValidate,
  required,
  size,
  hideLabel,
  noLabel,
  fieldErrors,
  hideErrors,
  requiredError,
  mask,
  stripMaskFromValue,
  numbersOnly,
  trimValue,
  isClearable,
  icon,
  iconClassName,
  dataAttrib,
  dataWrapperAttrib,
  ...rest
}) => {
  // -------------------------
  //     ALL THE MASKING
  // -------------------------

  // function to create the regex for the custom masks
  const customMaskCleanRegEx = arr => {
    // if the prop for the stripMaskFromValue is an array,
    // then the app must be defining the clean up
    if (Array.isArray(stripMaskFromValue))
      return new RegExp([...stripMaskFromValue].join("|"), "g");

    // if they didn't define it, then use the custom values in the mask prop
    return new RegExp(
      [...arr.filter(a => a.constructor === String)].join("|"),
      "g"
    );
  };

  // using the array of values in the mask...
  // create a regex that can be used to test
  const customMaskTestRegEx = arr => {
    const str = arr
      .map(i => {
        if (i.constructor === String) {
          return "\\" + i;
        }
        return i;
      })
      .join("");
    return new RegExp(`^${str.replace(/\//g, "")}$`);
  };

  const customMaskTestRegExStripped = arr => {
    if (!arr) return null;

    let str = "";
    arr
      .filter(i => i.constructor !== String)
      .forEach(r => {
        str = str + r;
      });

    return new RegExp(`^${str.replace(/\//g, "")}$`);
  };

  // a state to hold the mask object (if it's in use)
  const [maskToUse] = useState(
    mask
      ? Array.isArray(mask)
        ? {
            maskConfig: mask,
            replace: customMaskCleanRegEx(mask),
            replaceWith: "",
            regExTest: customMaskTestRegEx(mask),
            regExTestStripped: customMaskTestRegExStripped(mask)
          }
        : preDefinedMasks[mask]
      : null
  );

  // add the restriction to only allow numbers if the prop set it or if the mask requires it
  const [numberRestrict] = useState(numbersOnly || maskToUse?.isNumber);

  const defaultValue = mask
    ? conformToMask(value, maskToUse.maskConfig).conformedValue
    : value;
  // tempVal is used in conjunction with "numberCommas" and "currency" masks...
  // it is used to display the masked value while the "real" value is saved in the form's state
  const [tempVal, setTempVal] = useState(defaultValue);

  // maskShow is used in conjunction with the mask property...
  // when a field uses a mask and has no actual value, then we display the mask (using "showMask")
  // but when the user focus/blurs a field, we toggle the "showMask" property
  const [maskShow, setMaskShow] = useState(true);

  // a function used for masks with manual formatting
  const maskFormat = useCallback(
    value => {
      let newVal = !value
        ? ""
        : maskToUse?.isNumber
        ? maskToUse.process.format(parseFloat(value.replace(/,|\$/g, ""))) // replace is necessary if stripMaskFromValue is enabled to prevent isNaN
        : value;

      if (mask === "currencyWithDecimals") {
        const inputtedValueDecimals = value.match(/\.\d{1,2}|\./);
        const formattedValueDecimals = newVal.match(/\.\d{2}/);
        const hasDecimal = /\.{1}/.test(value);

        // if hasDecimal, then at the very least, the user typed a period, but it might have more, so...
        // if it has decimal places from the both the pure value and the formatted value, then do this...
        // ... we have to check the formatted value because of rounding: 11.999 becomes $12
        if (hasDecimal || inputtedValueDecimals || formattedValueDecimals) {
          // strip whatever parseFloat gave us for fraction digits
          newVal = newVal.replace(/\.\d+/, "");

          // here is where we detect if the new value is a rounded up variation 11.999 to 12
          const origInt = Number(value.replace(/,|\$|\.\d+/g, ""));
          const formattedInt = Number(newVal.replace(/,|\$|\.\d+/g, ""));
          // if it's different, then it means that it has been rounded up and there's actually no decimals needed

          // but if they are the same, then we need to include the decimals from the original input...
          // ... (because the formatted ones get truncated if they're zeros... eg. 456.0 becomes 456)
          if (origInt === formattedInt) {
            // concatenate the new decimals (formattedValueDecimals) based on the masked value (this accounts for rounding: 11.999 = $12)
            // if the new decimals are removed because it's a whole number and the user typed a ".", fallback to the period
            newVal += inputtedValueDecimals || ".";
          }
        }
      }

      return newVal;
    },
    [mask, maskToUse]
  );

  // for fields with manual formatting masks, we update the temp value when the "value" changes...
  // this covers the need for the update that happens when apps use the "updateForm" method
  // ...  instead of when the user changes the value
  useEffect(() => {
    const newVal = !value
      ? ""
      : mask && maskToUse?.maskConfig
      ? conformToMask(value, maskToUse.maskConfig).conformedValue
      : maskFormat(value);

    if (tempVal !== newVal) setTempVal(newVal);
  }, [mask, maskFormat, maskToUse, tempVal, value]);

  // function to use to provide the value of the input without the masking characters
  const valueWithoutMask = value => {
    if (!value) return "";

    const newValue = mask
      ? value.replace(maskToUse?.replace, maskToUse?.replaceWith)
      : value;
    return newValue;
  };

  // -------------------------
  //     EVENT HANDLERS
  // -------------------------

  const handleChange = event => {
    const fieldValue = event.currentTarget.value;
    const v = numberRestrict
      ? fieldValue.replace(/[^0-9\.]+/g, "")
      : fieldValue;

    let value = !valueWithoutMask(v)
      ? ""
      : stripMaskFromValue
      ? valueWithoutMask(v)
      : maskFormat(v);

    if (mask === "currencyWithDecimals") {
      if (/^.+\.\d/.test(value)) {
        value = value.replace(/(\.\d{1,2})(?:\.\d{1,2})+/g, "");
      }
    }

    // update mask value state so there's no flicker when the useEffect runs
    setTempVal(maskFormat(value));

    // update Form
    onChange({ event, field: name, value });
  };

  const handleBlur = event => {
    const origValue = event?.currentTarget?.value || "";
    const v = trimValue ? origValue.trim() : origValue;
    const value = stripMaskFromValue ? valueWithoutMask(v) : v;

    // if the value has white space at beginning or end of value,
    // ...trim it and update the input
    if (/^\s|\s$/.test(origValue) && trimValue) {
      onChange({ event, field: name, value });
    }

    onBlur({ event, field: name, value });

    // if we're using the MaskedInput, toggle the showing of the mask if the value is faslsy
    if (
      mask &&
      !maskToUse?.manualMask &&
      !valueWithoutMask(event.currentTarget.value)
    ) {
      setMaskShow(true);
    }
  };

  const handleFocus = event => {
    // if we're using the MaskedInput, toggle the showing of the mask if the value is faslsy
    if (
      mask &&
      !maskToUse?.manualMask &&
      !valueWithoutMask(event.currentTarget.value)
    ) {
      setMaskShow(false);
    }
  };

  const handleKeyDown = event => {
    if (onKeyDown) {
      const value = valueWithoutMask(event.currentTarget.value);
      onKeyDown({ event, value });
    }
  };

  const handleOnValidate = ({ field, value, validation }) => {
    const errors = onValidate({ field, value, validation });

    if (minLength) {
      const minimumLength = new RegExp(".{" + minLength + "}", "i");
      if (!minimumLength.test(value)) {
        errors.push(label + " must be at least " + minLength + " characters.");
      }
    }

    if (!!tempVal && mask && maskToUse?.regExTest) {
      if (!stripMaskFromValue && !maskToUse.regExTest.test(tempVal)) {
        errors.push(label + " is incomplete.");
      }
      if (
        stripMaskFromValue &&
        maskToUse.regExTestStripped &&
        !maskToUse.regExTestStripped.test(value)
      ) {
        errors.push(label + " is incomplete.");
      }
    }

    return errors;
  };

  const clearField = event => {
    onChange({ event, field: name, value: "" });
  };

  // -------------------------
  //     STYLES/CLAASES
  // -------------------------
  const sizeClass = size ? "uikit__input-size__" + size : null;

  const containerClasses = classnames(
    "uikit__form-item",
    "uikit__input-wrapper",
    className,
    sizeClass,
    { "uikit__hide-label": hideLabel, "uikit__form__hide-errors": hideErrors }
  );
  const inputClass = classnames({
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value,
    "mask-placeholder": !value && maskToUse?.maskConfig
  });

  // -------------------------
  //     FORM REGISTRATION
  // -------------------------

  useField({
    name,
    validation: { required, requiredError },
    onValidate: handleOnValidate,
    label
  });

  return (
    <div
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      {!noLabel && (
        <Label
          htmlFor={id}
          label={label}
          labelElement={labelElement}
          required={required}
        />
      )}
      {maskToUse?.maskConfig ? (
        <MaskedInput
          aria-describedby={
            !hideErrors && !!fieldErrors?.length && `errors_for_${id}`
          }
          key={id}
          type="text"
          mask={maskToUse.maskConfig}
          id={id}
          name={name}
          value={value}
          className={inputClass}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          placeholder={placeholder}
          onFocus={handleFocus}
          disabled={disabled}
          showMask={maskShow}
          {...createDataAttributes(dataAttrib)}
          {...rest}
        />
      ) : (
        <div
          className={classnames("uikit__input-input", {
            "uikit__input-clearable": isClearable,
            "uikit__input-has-icon": icon
          })}
        >
          <input
            aria-describedby={
              !hideErrors && !!fieldErrors?.length && `errors_for_${id}`
            }
            type="text"
            id={id}
            name={name}
            value={tempVal}
            className={inputClass}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={disabled}
            {...createDataAttributes(dataAttrib)}
            {...rest}
          />
          {isClearable && !!tempVal && (
            <button
              className="uikit__input-clearable-button"
              tabIndex={-1}
              onClick={clearField}
            >
              <Icon icon="fasTimes" />
            </button>
          )}

          {(!isClearable || (isClearable && !tempVal)) && icon && (
            <span
              className={classnames(
                "uikit__input__icon-wrapper",
                iconClassName
              )}
            >
              <Icon icon={icon} />
            </span>
          )}
        </div>
      )}
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

Input.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.oneOfType([propTypes.string, propTypes.number]).isRequired,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,
  isClearable: propTypes.bool,
  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,
  icon: propTypes.string,
  iconClassName: propTypes.string,

  //component-specific
  minLength: propTypes.number,
  maxLength: propTypes.number,
  placeholder: propTypes.string,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),

  mask: propTypes.oneOfType([
    propTypes.oneOf([
      "phone",
      "ssn",
      "ein",
      "currency",
      "currencyWithDecimals",
      "numberCommas"
    ]),
    propTypes.array
  ]),
  trimValue: propTypes.bool,
  stripMaskFromValue: propTypes.oneOfType([propTypes.bool, propTypes.array]),
  numbersOnly: propTypes.bool,
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object)
};

Input.defaultProps = {
  size: "auto",
  maxLength: 32,
  required: false,
  noLabel: false,
  hideErrors: false,
  numbersOnly: false,
  trimValue: true,
  stripMaskFromValue: true,
  componentname: "Input"
};

export default Input;
